export interface CallContextEntry {
    label: string;
    value: string;
}

const SENSITIVE_KEY = /pass|secret|token|auth|api[_-]?key|ssn|cvv|\bpin\b|card|account(?:[_-]?|\s*)num(?:ber)?/i;
const INVISIBLE_OR_CONTROL = /[\p{Cc}\p{Cf}]/gu;

function cleanText(value: string): string {
    return value.replace(INVISIBLE_OR_CONTROL, '').replace(/\s+/g, ' ').trim();
}

function truncate(value: string, maximumLength: number): string {
    const characters = Array.from(value);
    if (characters.length <= maximumLength) return value;
    const prefix = characters.slice(0, Math.max(0, maximumLength - 1)).join('');
    const wordBoundary = prefix.lastIndexOf(' ');
    return `${(wordBoundary > Math.floor(maximumLength / 2) ? prefix.slice(0, wordBoundary) : prefix).trimEnd()}…`;
}

function labelForKey(key: string): string | undefined {
    const cleanKey = cleanText(key);
    if (!cleanKey || SENSITIVE_KEY.test(cleanKey)) return undefined;
    const words = cleanKey
        .replace(/([a-z\d])([A-Z])/g, '$1 $2')
        .split(/[\s_-]+/)
        .filter(Boolean)
        .map(word => `${word.charAt(0).toUpperCase()}${word.slice(1)}`);
    if (!words.length) return undefined;
    return truncate(words.join(' '), 24);
}

/**
 * The protocol is a protobuf map, so integration ordering cannot survive the
 * wire. Render a bounded, deterministic label order instead of raw JSON.
 */
export function getCallContextEntries(popupInfo?: Record<string, string>, limit = 20): CallContextEntry[] {
    if (!popupInfo) return [];
    const entries: CallContextEntry[] = [];
    const seenLabels = new Set<string>();

    for (const [key, rawValue] of Object.entries(popupInfo)) {
        const label = labelForKey(key);
        if (!label || typeof rawValue !== 'string') continue;
        const value = cleanText(rawValue);
        const labelKey = label.toLocaleLowerCase();
        if (!value || seenLabels.has(labelKey)) continue;
        seenLabels.add(labelKey);
        entries.push({ label, value: truncate(value, 64) });
    }

    return entries.sort((left, right) => left.label.localeCompare(right.label)).slice(0, limit);
}

/** Returns the quiet, app-framed live transfer context, if it is safe to show. */
export function getTransferReasonLabel(transferReason?: string): string | undefined {
    if (typeof transferReason !== 'string') return undefined;
    const reason = cleanText(transferReason);
    return reason ? `Transferred: ${reason}` : undefined;
}
